type ResolveCallback<T> = (value?: T | PromiseLike<T>) => void;
type RejectCallback = (reason?: any) => void;

export class FetchTransportError extends Error {
    constructor(message?: string) {
        super(message); // 'Error' breaks prototype chain here
        Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
    }
}

// tslint:disable-next-line:max-classes-per-file
export class ServerError extends Error {
    constructor(message?: string) {
        super(message); // 'Error' breaks prototype chain here
        Object.setPrototypeOf(this, new.target.prototype); // restore prototype chain
    }
}

// tslint:disable-next-line:max-classes-per-file
export class ApiRequest {
    constructor(
        private requestMode?: RequestMode,
    ) {
    }

    public async get<T>(url: string): Promise<T> {
        const targetUrl = new URL(url, window.location.origin);
        const request: RequestInit = {
            method: "get",
            credentials: "omit",
            headers: [
                ["Accept", "application/json, text/plain, */*"],
                ["Content-Type", "application/json"],
            ],
        };

        return this.performRequestCore<T>(targetUrl.href, request);
    }

    public async postJson<TPayload, TResponse>(url: string, payload: TPayload) {
        const targetUrl = new URL(url, window.location.origin);
        const request: RequestInit = {
            method: "post",
            credentials: "omit",
            headers: [
                ["Accept", "application/json, text/plain, */*"],
                ["Content-Type", "application/json"],
            ],
            body: JSON.stringify(payload)
        };

        return this.performRequestCore<TResponse>(targetUrl.href, request);
    }

    private performRequestCore<T>(href: string, request: RequestInit): Promise<T> {
        if (this.requestMode) { 
            request.mode = this.requestMode;
        }

        return new Promise<T>((resolve: ResolveCallback<T>, reject: RejectCallback) => {
            fetch(href, request)
            .then(this.checkStatus)
            .then((response) => this.onRequestFulfilled(response, resolve))
            .catch((reason) => this.onFetchPromiseRejected(reason, reject));
        });
    }

    private async checkStatus(response: Response): Promise<Response> {
        if (response.ok) {
            return response;
        } else {
            throw new ServerError(response.statusText);
        }
    }

    private async onRequestFulfilled<T>(response: Response, resolve: ResolveCallback<T>): Promise<void> {
        const entity = (await response.json()) as T;
        resolve(entity);
    }

    private async onFetchPromiseRejected(reason: any, reject: RejectCallback) {
        if (reason instanceof TypeError) {
            // Could be network unavailable, CORS issue or wrong content type. Fetch
            // should not reject for anything else
            reject(new FetchTransportError());
        }

        reject(reason);
    }
}
